import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, ArrowRight, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { formatGBP } from '@/lib/utils';

export default function PaymentResult({ outcome }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const bookingId = params.get('booking_id');
  const sessionId = params.get('session_id');
  const [verifying, setVerifying] = useState(outcome === 'success');
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (outcome !== 'success' || !bookingId) return;
    api
      .get('/payments/verify-session', { params: { session_id: sessionId || '', booking_id: bookingId } })
      .then(({ data }) => setInfo(data))
      .catch(() => {})
      .finally(() => setVerifying(false));
  }, [outcome, bookingId, sessionId]);

  const success = outcome === 'success';
  const Icon = success ? CheckCircle2 : XCircle;
  const tone = success ? 'text-emerald-600' : 'text-rose-600';
  const bg = success ? 'from-emerald-500 to-emerald-600' : 'from-rose-500 to-rose-600';

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <Card className="w-full max-w-md p-6 border-slate-200 text-center space-y-3">
        <div className={`mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br ${bg} flex items-center justify-center`}>
          <Icon className="h-8 w-8 text-white" />
        </div>
        <h1 className={`text-2xl font-bold ${tone}`}>{success ? 'Payment received' : 'Payment cancelled'}</h1>
        <p className="text-sm text-slate-600">
          {success
            ? 'Your booking is confirmed and the partner has been notified.'
            : 'No charge was made. You can retry from your bookings.'}
        </p>
        {verifying && (
          <p className="text-xs text-slate-500"><Loader2 className="h-3 w-3 animate-spin inline" /> Verifying payment...</p>
        )}
        {info && info.total_amount != null && (
          <p className="text-sm text-slate-700">Total: <span className="font-semibold">{formatGBP(info.total_amount)}</span> · {info.payment_status}</p>
        )}
        <div className="flex flex-col gap-2 pt-2">
          <Button onClick={() => navigate('/customer/bookings')} className="rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="payment-to-bookings">
            View my bookings <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
          <Button variant="outline" onClick={() => navigate('/customer')} className="rounded-full" data-testid="payment-to-home">
            Back to Home
          </Button>
        </div>
      </Card>
    </div>
  );
}
